import { useEffect, useMemo } from 'react'
import { Modal } from '@trybeapp/ui.modal'
import { Formik, useFormikContext, ErrorMessage, useField } from 'formik'
import { Button } from '@trybeapp/ui.button'
import { string, object, number, array } from 'yup'
import {
  APPOINTMENT_SELECTION_STAGE,
  DEFAULT_STAGE,
  TIMES_STAGE,
  useAddAppointmentOrderItemOverlay,
  useNewItem,
  useStage,
} from '../contexts'
import { Hint } from 'ui/components/Hint'
import { useCurrentOrder } from 'contexts/Order'
import { Text } from 'components/Text'
import { Box } from '@trybeapp/ui.box'
import { NextButton, SelectPill } from '..'
import { Alert } from '@trybeapp/ui.alert'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { AppointmentTypeProvider, useAppointmentType } from 'contexts/AppointmentType'
import { useIsEditMode } from '../hooks'
import { useOrderId } from 'screens/OrderDetails/components/Overview/hooks'
import { STATUS_CONFIRMED } from 'constants/orderStatuses'
import DeleteButton from 'components/DeleteButton'
import { trackEvent } from 'components/Segment'
import {
  EVENT_EDIT_APPOINTMENT_STEP_COMPLETED,
  EVENT_ADD_APPOINTMENT_STEP_COMPLETED,
  EVENT_EDIT_APPOINTMENT_CANCELLED,
  EVENT_ADD_APPOINTMENT_CANCELLED,
} from 'constants/trackableEvents'
import { FormikGuestSelection } from 'components/GuestSelection'
import { useDeleteOrderItem } from 'api/OrderItems'
import { useAppointmentTypes } from 'api/AppointmentTypes'
import { OfferingSearch } from 'components/OfferingSearch'
import { TYPE_APPOINTMENT } from 'constants/offeringTypes'

export const DefaultStage = () => {
  const { setStage } = useStage()
  const { order: { id: orderId, guests: guestIds = [] } = {} } = useCurrentOrder()
  const {
    newItem: { type_id: newItemTypeId = '', ...newItem },
    setNewItem,
  } = useNewItem()

  const hasGuests = useMemo(() => guestIds.length > 0, [guestIds.length])
  const isEditMode = useIsEditMode()

  const initialValues = {
    guest_ids: newItem.guest_ids ?? [],
    type_id: newItemTypeId,
  }

  const validationSchema = useMemo(
    () =>
      object({
        type_id: string().required('You must select an appointment'),
        duration: number(),
        guest_ids: hasGuests ? array().required('You must select a guest') : undefined,
      }),
    [hasGuests]
  )

  const handleOnSubmit = (values) => {
    setNewItem({ ...newItem, ...values })
    setStage(TIMES_STAGE)
    if (isEditMode) {
      trackEvent(EVENT_EDIT_APPOINTMENT_STEP_COMPLETED, {
        step: APPOINTMENT_SELECTION_STAGE,
        orderId,
        orderItemId: newItem.id,
      })
    } else {
      trackEvent(EVENT_ADD_APPOINTMENT_STEP_COMPLETED, {
        step: APPOINTMENT_SELECTION_STAGE,
        orderId,
      })
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validationSchema={validationSchema}
    >
      <ModalContents />
    </Formik>
  )
}

const ModalContents = () => {
  const { modal } = useAddAppointmentOrderItemOverlay()
  const isEditMode = useIsEditMode()
  const orderId = useOrderId()
  const { newItem } = useNewItem()

  const closeModal = () => {
    modal.hide()

    if (isEditMode) {
      trackEvent(EVENT_EDIT_APPOINTMENT_CANCELLED, {
        step: DEFAULT_STAGE,
        orderId,
        orderItemId: newItem.id,
      })
    } else {
      trackEvent(EVENT_ADD_APPOINTMENT_CANCELLED, {
        step: DEFAULT_STAGE,
        orderId,
      })
    }
  }

  return (
    <>
      <Modal.Header backgroundColor="light.800">
        {isEditMode ? 'Edit' : 'Add'} appointment
      </Modal.Header>
      <Modal.Content overflow="scroll">
        <FormikGuestSelection orderId={orderId} name="guest_ids" max={null} />
        <AppointmentSelection />
      </Modal.Content>
      <Modal.Footer>
        <Box width={1} display="flex">
          <NextButton />
          <Box ml="auto">
            <DeleteAppointmentButton />
            <Button variant="ghost" onClick={closeModal}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal.Footer>
    </>
  )
}

const DeleteAppointmentButton = () => {
  const exists = useIsEditMode()
  const {
    newItem: { id: itemId, status },
  } = useNewItem()

  const orderId = useOrderId()

  const { mutate, isLoading } = useDeleteOrderItem()

  const handleOnDelete = () => {
    mutate([orderId, itemId])
  }

  if (!exists) return null

  return (
    <DeleteButton
      mx="sm"
      size="sm"
      variant="secondary-danger"
      loading={isLoading}
      onDelete={handleOnDelete}
      text={status === STATUS_CONFIRMED ? 'Cancel' : 'Delete'}
    />
  )
}

const AppointmentSelection = () => {
  const siteId = useCurrentSiteId()
  const { submitForm } = useFormikContext()
  const [{ value }, , { setValue }] = useField('type_id')

  const { data: { data: appointmentTypes = [] } = {}, isSuccess } = useAppointmentTypes(
    { perPage: -1, siteId },
    { refetchOnWindowFocus: false, refetchOnMount: false }
  )

  const unavailable = isSuccess && value && !appointmentTypes.find(({ id }) => id === value)

  return (
    <Box mb="md">
      <Text mb="sm">Select an appointment:</Text>
      {unavailable && <UnavailableAppointmentAlert />}
      <div className="mb-2">
        <OfferingSearch
          offeringType={TYPE_APPOINTMENT}
          onSelect={(offering) => {
            setValue(offering.id)
            setTimeout(() => submitForm(), 50)
          }}
        />
      </div>
      {appointmentTypes.map((appointmentType) => (
        <AppointmentTypeProvider appointmentType={appointmentType} key={appointmentType.id}>
          <Appointment />
        </AppointmentTypeProvider>
      ))}
      <ErrorMessage name="type_id">{(error) => <Hint variant="error">{error}</Hint>}</ErrorMessage>
    </Box>
  )
}

const UnavailableAppointmentAlert = () => {
  return (
    <Alert variant="error" mb="sm">
      <Box>
        It looks like the appointment type you are editing is no longer available. Click next to
        edit the times, but availability cannot be checked.
      </Box>
    </Alert>
  )
}

const Appointment = () => {
  const {
    name,
    id: appointmentId,
    durations: appointmentDurations = [],
    max_guests: maxGuests,
    min_guests: minGuests,
  } = useAppointmentType()

  const {
    values: { type_id: typeId = '', guest_ids: guestIds },
    setFieldValue,
    validateForm,
  } = useFormikContext()

  const { order = {} } = useCurrentOrder()

  const hasGuests = order.guests.length > 0
  const selected = typeId === appointmentId
  const minOk = (minGuests === 1 && !hasGuests) || guestIds.length >= minGuests
  const maxOk = (maxGuests === 1 && !hasGuests) || guestIds.length <= maxGuests
  const disabled = !minOk || !maxOk

  useEffect(() => {
    if (selected && disabled) {
      setFieldValue('type_id', null)
    }
  }, [selected, disabled, setFieldValue])

  const handleOnClick = () => {
    setFieldValue('type_id', appointmentId)
    setFieldValue('duration', appointmentDurations[0])

    // TODO: without this line Formik thinks the type_id field is invalid even
    // though it seems to know it has a value and the form is dirty
    setTimeout(() => {
      validateForm()
    }, 0)
  }

  return (
    <SelectPill selected={selected} onClick={handleOnClick} disabled={disabled}>
      {name}
      <Text as="span" display="inline" fontSize="0.8em" ml="2" color="nude.500">
        {appointmentDurations[0]} minutes
      </Text>
    </SelectPill>
  )
}
